/**
 * What the project chat knows, built from the relay's events alone (`applyChatEvents`): the messages in the
 * relay's order, the reactions, how far each person has read and who is typing. Pure and immutable, so the
 * history replayed after a reconnect lands on what is already there without doubling anything: a message is
 * its id, a reaction is one person's one emoji on a message, a read mark only moves forward.
 */
import { CHAT_HISTORY_LIMIT, TYPING_TTL_MS, type ChatIncoming, type ChatReaction } from './protocol';

export interface ChatMessage {
  id: string; authorId: string; authorName: string; text: string; replyTo: string | null;
  /** The sender's clock: the time the message shows. */
  sentAt: number;
  /** The relay's clock: the message's place in the history. */
  at: number;
  editedAt: number | null; deleted: boolean;
  /** An own message the relay has not confirmed yet: on its way, or it could not leave. */
  local?: 'pending' | 'failed';
}
/** How far someone has read: up to and including this message. */
export type ReadMark = { messageId: string; at: number };
export interface ChatState {
  readonly messages: readonly ChatMessage[];
  /** Message → who reacted → with what, in the order they last did: one reaction per person, as in Instagram. */
  readonly reactions: Readonly<Record<string, Readonly<Record<string, ChatReaction>>>>;
  readonly reads: Readonly<Record<string, ReadMark>>;
  /** Who is typing, until when (this browser's clock). */
  readonly typing: Readonly<Record<string, number>>;
  /** The latest name each participant came with. */
  readonly names: Readonly<Record<string, string>>;
}

export const emptyChat = (): ChatState => ({ messages: [], reactions: {}, reads: {}, typing: {}, names: {} });

export const compareMarks = (a: ReadMark, b: ReadMark): number => a.at - b.at || (a.messageId < b.messageId ? -1 : a.messageId > b.messageId ? 1 : 0);
export const markOf = (message: ChatMessage): ReadMark => ({ messageId: message.id, at: message.at });

/** Where a message with this place goes among messages kept in order. */
function slotFor(messages: readonly ChatMessage[], mark: ReadMark): number {
  let low = 0, high = messages.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (compareMarks(markOf(messages[middle]), mark) < 0) low = middle + 1; else high = middle;
  }
  return low;
}

/**
 * The events from the relay, applied in the order they came. Returns the same object when nothing changed, so a
 * duplicate from a replay re-renders nothing. `now` is this browser's clock, for the typing flags only.
 */
export function applyChatEvents(state: ChatState, events: readonly ChatIncoming[], now: number, limit = CHAT_HISTORY_LIMIT): ChatState {
  let messages = state.messages as ChatMessage[], reactions = state.reactions as Record<string, Record<string, ChatReaction>>;
  let reads = state.reads as Record<string, ReadMark>, typing = state.typing as Record<string, number>, names = state.names as Record<string, string>;
  let changed = false;
  const own = { messages: false, reactions: false, reads: false, typing: false, names: false };
  const write = <K extends keyof typeof own>(part: K) => {
    changed = true;
    if (own[part]) return;
    own[part] = true;
    if (part === 'messages') messages = [...messages];
    else if (part === 'reactions') reactions = { ...reactions };
    else if (part === 'reads') reads = { ...reads };
    else if (part === 'typing') typing = { ...typing };
    else names = { ...names };
  };
  const advance = (userId: string, mark: ReadMark) => {
    const previous = reads[userId];
    if (previous && compareMarks(previous, mark) >= 0) return;
    write('reads'); reads[userId] = mark;
  };
  const stopTyping = (userId: string) => { if (userId in typing) { write('typing'); delete typing[userId]; } };

  for (const event of events) {
    if (names[event.userId] !== event.displayName) { write('names'); names[event.userId] = event.displayName; }
    if (event.type === 'ChatMessage') {
      const data = event.data, index = messages.findIndex(message => message.id === data.id);
      if (index >= 0) {
        const existing = messages[index];
        // Only the author changes a message, and a deleted one stays deleted.
        if (existing.authorId !== event.userId || existing.deleted) continue;
        let next: ChatMessage = existing;
        if (data.deleted) next = { ...existing, text: '', deleted: true, editedAt: data.editedAt ?? existing.editedAt, local: undefined };
        else if (data.editedAt !== undefined && data.editedAt > (existing.editedAt ?? -1)) next = { ...existing, text: data.text, editedAt: data.editedAt, local: undefined };
        else if (existing.local) next = { ...existing, at: event.at, local: undefined };
        if (next === existing) continue;
        write('messages');
        messages.splice(index, 1);
        // A confirmation takes the relay's place for the message; an edit keeps the one it had.
        messages.splice(slotFor(messages, markOf(next)), 0, next);
        if (next.deleted && reactions[next.id]) { write('reactions'); delete reactions[next.id]; }
        advance(event.userId, markOf(next));
        continue;
      }
      const message: ChatMessage = { id: data.id, authorId: event.userId, authorName: event.displayName, text: data.deleted ? '' : data.text,
        replyTo: data.replyTo, sentAt: data.sentAt, at: event.at, editedAt: data.editedAt ?? null, deleted: !!data.deleted };
      write('messages');
      messages.splice(slotFor(messages, markOf(message)), 0, message);
      stopTyping(event.userId);
      // Writing a message is reading everything before it.
      advance(event.userId, markOf(message));
    } else if (event.type === 'ChatReaction') {
      const { messageId, emoji } = event.data;
      const target = messages.find(message => message.id === messageId);
      if (!target || target.deleted || (reactions[messageId]?.[event.userId] ?? null) === emoji) continue;
      write('reactions');
      // A new reaction replaces the person's old one and goes last, as the latest.
      const entry = { ...reactions[messageId] };
      delete entry[event.userId];
      if (emoji) entry[event.userId] = emoji;
      if (Object.keys(entry).length) reactions[messageId] = entry; else delete reactions[messageId];
    } else if (event.type === 'ChatRead') {
      advance(event.userId, { messageId: event.data.messageId, at: event.data.at });
    } else if (event.type === 'ChatTyping') {
      if (event.data.typing) { write('typing'); typing[event.userId] = now + TYPING_TTL_MS; }
      else stopTyping(event.userId);
    }
  }
  if (messages.length > limit) {
    const dropped = messages.slice(0, messages.length - limit);
    write('messages'); messages = messages.slice(-limit);
    for (const message of dropped) if (reactions[message.id]) { write('reactions'); delete reactions[message.id]; }
  }
  return changed ? { messages, reactions, reads, typing, names } : state;
}

/** An own message before the relay has it: at the end of the history, however the clocks differ. */
export function addLocalMessage(state: ChatState, message: Omit<ChatMessage, 'at' | 'local'>, now: number): ChatState {
  const last = state.messages.at(-1);
  const local: ChatMessage = { ...message, at: last && last.at >= now ? last.at + 1 : now, local: 'pending' };
  return { ...state, messages: [...state.messages, local] };
}

/** An own unconfirmed message changes state: sent again, failed, or confirmed by the transport without an echo. */
export function setLocalStatus(state: ChatState, id: string, status: ChatMessage['local']): ChatState {
  const index = state.messages.findIndex(message => message.id === id);
  if (index < 0 || !state.messages[index].local || state.messages[index].local === status) return state;
  const messages = [...state.messages];
  messages[index] = { ...messages[index], local: status };
  return { ...state, messages };
}

/** A message that never left, taken back. */
export function dropLocalMessage(state: ChatState, id: string): ChatState {
  const message = state.messages.find(candidate => candidate.id === id);
  return message?.local ? { ...state, messages: state.messages.filter(candidate => candidate !== message) } : state;
}

/** Typing flags whose time is up, gone. */
export function expireTyping(state: ChatState, now: number): ChatState {
  const expired = Object.entries(state.typing).filter(([, until]) => until <= now);
  if (!expired.length) return state;
  const typing = { ...state.typing };
  for (const [user] of expired) delete typing[user];
  return { ...state, typing };
}

const isUnread = (state: ChatState, message: ChatMessage, selfId: string): boolean => {
  if (message.authorId === selfId || message.deleted) return false;
  const mark = state.reads[selfId];
  return !mark || compareMarks(markOf(message), mark) > 0;
};
/** Messages from the others after the point you have read to. */
export const unreadCount = (state: ChatState, selfId: string): number => state.messages.reduce((count, message) => count + (isUnread(state, message, selfId) ? 1 : 0), 0);
/** The first of them, where the «Новые сообщения» line goes. */
export const firstUnread = (state: ChatState, selfId: string): string | null => state.messages.find(message => isUnread(state, message, selfId))?.id ?? null;

/** Who has read a message, the author aside, in the order their marks came. */
export function readersOf(state: ChatState, message: ChatMessage): string[] {
  if (message.local) return [];
  return Object.entries(state.reads).filter(([user, mark]) => user !== message.authorId && compareMarks(mark, markOf(message)) >= 0).map(([user]) => user);
}

/** Where each of the others has read to: the message their mark stands on, or the last one before it. */
export function readHeads(state: ChatState, selfId: string): Map<string, string[]> {
  const heads = new Map<string, string[]>();
  for (const [user, mark] of Object.entries(state.reads)) {
    if (user === selfId) continue;
    const index = slotFor(state.messages, mark);
    const at = state.messages[index]?.id === mark.messageId ? index : index - 1;
    const message = state.messages[at];
    if (!message) continue;
    heads.set(message.id, [...heads.get(message.id) ?? [], user]);
  }
  return heads;
}

/** What a message's reaction pill shows: up to three kinds, the most chosen first, and how many reacted; null without any. */
export function reactionSummary(state: ChatState, messageId: string): { top: ChatReaction[]; total: number; people: [string, ChatReaction][] } | null {
  const entry = state.reactions[messageId];
  const people = entry ? Object.entries(entry) : [];
  if (!people.length) return null;
  const counts = new Map<ChatReaction, number>();
  for (const [, emoji] of people) counts.set(emoji, (counts.get(emoji) ?? 0) + 1);
  // Map keeps the order kinds first came in, and sort is stable: equal counts keep it.
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([emoji]) => emoji);
  return { top, total: people.length, people };
}

/** Who is typing now, the others only. */
export const typingUsers = (state: ChatState, now: number, selfId: string): string[] =>
  Object.entries(state.typing).filter(([user, until]) => user !== selfId && until > now).map(([user]) => user);

/** What an own message shows after its time: the clock, one tick, two ticks, or that it did not leave. */
export function ownStatus(state: ChatState, message: ChatMessage): 'pending' | 'failed' | 'sent' | 'read' {
  if (message.local) return message.local;
  return readersOf(state, message).length ? 'read' : 'sent';
}
